import {
  SITUATIONS,
  annualRate,
  blockAvailable,
  money,
  monthLabel,
  percent,
} from "./format";
import { LockedCell, Note } from "./Cells";

const DEFAULT_PATTERN_HINT = "Padrão de meta: venda distribuída em 24 meses a partir do mês seguinte, recebida no mês da venda.";

const irrTitle = (metrics, rateText) => {
  if (metrics?.irrAmbiguous) return "O fluxo muda de sinal várias vezes e a TIR fica ambígua: use o VPL";
  if (!Number.isSafeInteger(metrics?.irrBps)) return "Não se aplica: o fluxo não troca de sinal";
  return `Contra ${rateText} de custo de capital`;
};

const IrrValue = ({ metrics }) => (metrics?.irrAmbiguous
  ? <span className="director-dashboard-cell__text">Use o VPL</span>
  : annualRate(metrics?.irrBps));

const GoalCell = ({ goal, hasStock, rateText }) => {
  if (!goal) return "—";
  if (goal.reached) return <span className="director-dashboard-cell__text" data-tone="good">Atingida</span>;
  if (hasStock && Number.isSafeInteger(goal.stockCents)) {
    return <>
      <span className="director-dashboard-cell__text">
        {`${money(goal.cents)} de ${money(goal.stockCents)} em estoque (${percent(goal.coverageBps)})`}
      </span>
      {goal.exceedsStock && <Note tone="bad">{`o estoque não paga a obra a ${rateText}`}</Note>}
    </>;
  }
  return <span className="director-dashboard-cell__goal" title={goal.usesDefaultPattern ? DEFAULT_PATTERN_HINT : undefined}>
    <span className="director-dashboard-cell__text director-dashboard-cell__text--strong">{money(goal.cents)}</span>
    <Note>{`para empatar a ${rateText}`}</Note>
  </span>;
};

// Meses no vermelho, com o aporte do pior momento; sem aporte, os meses não pedem dinheiro novo.
const gapText = (gap) => {
  if (!gap?.months) return "Nenhum";
  return gap.worstCents < 0 ? `${gap.months} m · ${money(-gap.worstCents)}` : `${gap.months} m · sem aporte`;
};

const ObraNotes = ({ row }) => <>
  {row.situation === "check_cost" && <Note tone="warn">
    {`Falta pagar só ${money(row.remaining?.outCents)} contra ${money(row.remaining?.inCents)} a receber: o custo da obra pode estar em outra SPE ou fora do Sienge.`}
  </Note>}
  {row.conditionDefaulted && <Note>condição não informada: à vista</Note>}
</>;

const ObrasTable = ({ data, rows, focus, onFocus }) => {
  const totals = data.totals || {};
  const rateText = annualRate(data.rate?.bps);
  const returns = blockAvailable(data, "returns");
  const liquidity = blockAvailable(data, "liquidity");
  const totalLocked = !Number.isSafeInteger(totals.npvCents);

  const select = (row) => onFocus(focus === row.key ? null : row.key);

  return <section className="director-section director-dashboard-obras" aria-labelledby="director-dashboard-obras-title">
    <header>
      <div>
        <span className="director-eyebrow">Resultado por obra</span>
        <h2 id="director-dashboard-obras-title">Quem dá lucro, quem consome caixa</h2>
      </div>
      <p>Clique numa obra para ver o caixa dela no gráfico abaixo.</p>
    </header>
    <div className="director-table-scroll">
      <table className="director-table director-dashboard-table" aria-label="Indicadores por obra">
        <thead>
          <tr>
            <th scope="col">Obra</th>
            {returns && <>
              <th scope="col">Meta de vendas</th>
              <th scope="col">Lucro projetado</th>
              <th scope="col">{`VPL a ${rateText}`}</th>
              <th scope="col">TIR</th>
            </>}
            {liquidity && <>
              <th scope="col">Exposição máxima</th>
              <th scope="col">Payback</th>
              <th scope="col">Desencaixe 12 meses</th>
            </>}
            <th scope="col">Situação</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const metrics = row.metrics || {};
            const open = row.hasStock && !row.overhead;
            const situation = SITUATIONS[row.situation];
            return <tr
              key={row.key}
              className="director-dashboard-table__row"
              data-focused={focus === row.key ? "true" : "false"}
              tabIndex={0}
              onClick={() => select(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  select(row);
                }
              }}
            >
              <th scope="row">
                <span className="director-dashboard-cell__name">{row.name}</span>
                <ObraNotes row={row} />
              </th>
              {returns && <>
                <td>{row.overhead ? "—" : <GoalCell goal={row.goal} hasStock={row.hasStock} rateText={rateText} />}</td>
                <td data-tone={open && metrics.totalCents < 0 ? "bad" : undefined}>
                  {row.overhead ? "—" : open ? money(metrics.totalCents) : <LockedCell />}
                </td>
                <td data-tone={open && metrics.npvCents < 0 ? "bad" : undefined}>
                  {row.overhead ? "—" : open ? money(metrics.npvCents) : <LockedCell />}
                </td>
                <td title={open ? irrTitle(metrics, rateText) : undefined}>
                  {row.overhead ? "—" : open ? <IrrValue metrics={metrics} /> : <LockedCell />}
                </td>
              </>}
              {liquidity && <>
                <td>
                  {metrics.exposureCents > 0 ? <>
                    <span className="director-dashboard-cell__text">{money(metrics.exposureCents)}</span>
                    <Note>{monthLabel(metrics.exposureMonth)}</Note>
                  </> : "Nenhuma"}
                </td>
                <td>{metrics.paybackMonth ? monthLabel(metrics.paybackMonth) : (metrics.exposureCents > 0 ? "Ainda não" : "—")}</td>
                <td data-tone={metrics.gap?.worstCents < 0 ? "bad" : undefined}>
                  {gapText(metrics.gap)}
                </td>
              </>}
              <td>
                {situation && <span className="director-dashboard-pill" data-tone={situation.tone}>{situation.label}</span>}
              </td>
            </tr>;
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">
              <span className="director-dashboard-cell__name">{data.scopeMode === "all" ? "Tudo" : "Total das obras"}</span>
              <Note>Exposição e desencaixe somam obra a obra: SPEs diferentes não compensam caixa.</Note>
            </th>
            {returns && <>
              <td>{totals.goalCents > 0 ? money(totals.goalCents) : "Atingida"}</td>
              <td>{totalLocked ? <LockedCell /> : money(totals.totalCents)}</td>
              <td>{totalLocked ? <LockedCell /> : money(totals.npvCents)}</td>
              <td>{totalLocked ? <LockedCell /> : <IrrValue metrics={totals} />}</td>
            </>}
            {liquidity && <>
              <td>{totals.exposureCents > 0 ? money(totals.exposureCents) : "Nenhuma"}</td>
              <td>—</td>
              <td>{gapText(totals.gap)}</td>
            </>}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  </section>;
};

export default ObrasTable;
